import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import type { IControlOption } from '@/Interfaces/ControlOption.interface';
import type { ICreativeFilters } from '@/Interfaces/Creatives/Creative.interface';
import { IParsedSingleSegment } from '@/Interfaces/Segments/Segment.interface';
import CreativesGrid from '@/components/Grid/Creatives';
//#region TODO: extract to common components and reuse
import CreativesModalButtons from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal/components/CreativesModalButtons';
import CreativesModalHeader from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal/components/CreativesModalHeader';
import CreativesModalSearch from '@/pages/CampaignManagement/CreateCampaign/LineItemForm/Creatives/Modal/components/CreativesModalSearch';
//#endregion
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectSegmentsCreatives } from '@/store/selectors/segments.selectors';
import { resetCreativesFilters, setCreativesFilters } from '@/store/slices/segments.slice';
import { getCreatives } from '@/store/slices/segments.thunks';
import { Dialog, DialogContent } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import { ChangeEvent, memo, useCallback, useEffect, useState } from 'react';
import columns from './columns';

interface IProps {
  advertiser: IControlOption;
  open: boolean;
  onClose: () => void;
  associatedCreatives: IParsedSingleSegment['creatives'];
  onAssociateCreatives: (creatives: IParsedSingleSegment['creatives']) => void;
}

const typeOptions = Object.values(CreativeType);

const AssociateCreativesModal = memo(
  ({ advertiser, open, onClose, associatedCreatives, onAssociateCreatives }: IProps) => {
    const dispatch = useAppDispatch();

    const { creatives, loadingCreatives, creativeFilters, total } = useAppSelector(selectSegmentsCreatives);

    const [selectedTypes, setSelectedTypes] = useState<CreativeType[]>(typeOptions);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedCreativesIds, setSelectedCreativeIds] = useState<GridRowSelectionModel>([]);

    const getCreativesData = useCallback(
      () => dispatch(getCreatives({ advertiserId: advertiser.value })),
      [dispatch, advertiser],
    );

    const resetFilters = useCallback(() => dispatch(resetCreativesFilters()), [dispatch]);

    const onFiltersChange = useCallback(
      (newFilters: Partial<ICreativeFilters>) => {
        dispatch(setCreativesFilters(newFilters));
      },
      [dispatch],
    );

    useEffect(() => {
      if (open) {
        getCreativesData();
      }
    }, [creativeFilters, getCreativesData, open]);

    useEffect(() => {
      if (open) {
        setSelectedCreativeIds(associatedCreatives.map(c => c.id));
      }
    }, [open, associatedCreatives]);

    const handleSearch = useCallback(
      debounce(newSearch => {
        dispatch(setCreativesFilters({ search: newSearch }));
      }, 500),
      [dispatch],
    );

    const handleSearchValueChange = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(value);
        handleSearch(value);
      },
      [handleSearch],
    );

    const handleTypeChange = useCallback(
      (value: CreativeType) => {
        const updatedSelectedTypes = selectedTypes.includes(value)
          ? selectedTypes.filter(type => type !== value)
          : [...selectedTypes, value];

        setSelectedTypes(updatedSelectedTypes);
        dispatch(setCreativesFilters({ types: updatedSelectedTypes }));
      },
      [dispatch, selectedTypes],
    );

    const handleClose = useCallback(() => {
      setSearchValue('');
      setSelectedTypes(typeOptions);
      onClose();
      setSelectedCreativeIds([]);
      resetFilters();
    }, [onClose, typeOptions]);

    const onSave = () => {
      onAssociateCreatives(selectedCreativesIds.map(id => ({ id: id as number })));
      handleClose();
    };

    return (
      <>
        <Dialog open={open} maxWidth={false}>
          <DialogContent sx={{ width: '60vw', maxWidth: '1100px' }}>
            <CreativesModalHeader handleClose={handleClose} />
            <CreativesModalSearch
              searchValue={searchValue}
              typeOptions={typeOptions}
              selectedTypes={selectedTypes}
              handleSearch={handleSearchValueChange}
              handleTypeChange={handleTypeChange}
              onRefreshClick={getCreativesData}
            />
            <CreativesGrid
              loading={loadingCreatives}
              rows={creatives}
              rowCount={total}
              columns={columns}
              checkboxSelection
              rowSelectionModel={selectedCreativesIds}
              onRowSelectionModelChange={setSelectedCreativeIds}
              selectedCreativesTypes={selectedTypes}
              onFiltersChange={onFiltersChange}
              sx={{ marginTop: '8px' }}
              keepNonExistentRowsSelected
            />
            <CreativesModalButtons handleClose={handleClose} handleSave={onSave} saveButtonTitle='Confirm' />
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

export default AssociateCreativesModal;
